// src/pages/analytics/AnalyticsAllCallsPage_with_UniversalTable.jsx

import { useAllCallsData } from '../../hooks/useAllCallsData';
import Pagination from '../../components/Pagination';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import TextModal from '../../components/sounds/TextModal';
import UniversalTable from '../../components/UniversalTable';
import {useState} from "react";

const AnalyticsAllCallsPage = () => {
  const {
    // Данные
    calls,
    themes,
    totalRecords,
    project,
    user,
    // Фильтры
    filters,
    themeFilters,
    dateFilters,
    tempTextValues,
    tempDateValues,
    // Обработчики
    handleTextChange,
    handleTextBlur,
    handleTextKeyDown,
    handleFilterChange,
    handleThemeFilterChange,
    resetFilters,
    handleDateChange,
    handleDateBlur,
    exportToExcel,
    // Пагинация
    currentPage,
    setCurrentPage,
    totalPages,
    start,
    end,
    // Состояние
    isFetching,
    perPage,
    // Экспорт
    isExporting,
    exportProgress,
    exportError,
    // Модалка текста
    activeTextSoundId,
    setActiveTextSoundId,
  } = useAllCallsData();

  // === Состояние сортировки ===
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    setSortField(field);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  // === Формирование заголовков ===
  const headers = [
    {
      label: 'Дата',
      key: 'date',
      // sortable: true,
      sortDirection: sortField === 'date' ? sortDirection : undefined,
      onSort: () => handleSort('date'),
      render: (row) => new Date(row.date).toLocaleDateString('ru-RU'),
    },
    {
      label: 'Время',
      key: 'date',
      render: (row) => new Date(row.date).toLocaleTimeString('ru-RU'),
    },
    {
      label: 'Телефон',
      key: 'phone',
      searchable: true,
      searchValue: tempTextValues.search_phone,
      onSearchChange: (value) => handleTextChange({ target: { name: 'search_phone', value } }),
      onSearchKeyDown: handleTextKeyDown,
      onSearchBlur: (value) => handleTextBlur({ target: { name: 'search_phone', value } }),
      render: (row) => row.phone.slice(-10),
    },
    {
      label: 'Оператор',
      key: 'operator',
      searchable: true,
      searchValue: tempTextValues.search_operator,
      onSearchChange: (value) => handleTextChange({ target: { name: 'search_operator', value } }),
      onSearchKeyDown: handleTextKeyDown,
      onSearchBlur: (value) => handleTextBlur({ target: { name: 'search_operator', value } }),
    },
    {
      label: 'Длительность',
      key: 'time_sound',
      filter: {
        type: 'select',
        value: filters.duration_op,
        onChange: (value) => handleFilterChange({ target: { name: 'duration_op', value } }),
        options: [
          { value: '', label: 'Все' },
          { value: 'gt', label: '>' },
          { value: 'lt', label: '<' },
        ],
      },
      searchable: true,
      searchValue: tempTextValues.duration_value,
      onSearchChange: (value) => handleTextChange({ target: { name: 'duration_value', value } }),
      onSearchKeyDown: handleTextKeyDown,
      onSearchBlur: (value) => handleTextBlur({ target: { name: 'duration_value', value } }),
      render: (row) => row.time_sound,
    },
    {
      label: 'Сброс',
      key: 'stopside',
      filter: {
        type: 'select',
        value: filters.filter_stopside,
        onChange: (value) => handleFilterChange({ target: { name: 'filter_stopside', value } }),
        options: [
          { value: '', label: 'Все' },
          { value: 'a', label: 'Клиент' },
          { value: 'b', label: 'Оператор' },
        ],
      },
      render: (row) => (row.stopside === 'a' ? 'Клиент' : 'Оператор'),
    },
    {
      label: 'Текст',
      key: 'text',
      searchable: true,
      searchValue: tempTextValues.search_text,
      onSearchChange: (value) => handleTextChange({ target: { name: 'search_text', value } }),
      onSearchKeyDown: handleTextKeyDown,
      onSearchBlur: (value) => handleTextBlur({ target: { name: 'search_text', value } }),
      render: (row) => (
        <button
          onClick={(e) => {
            e.preventDefault();
            setActiveTextSoundId(row.id);
          }}
          className="text-link-button"
        >
          {row.text.split('оператора: ').pop()?.substring(0, 40)}...
        </button>
      ),
    },
    ...themes.map((theme) => ({
      label: theme.name,
      key: `theme_${theme.id}`,
      filter: {
        type: 'select',
        value: themeFilters[theme.id] || '',
        onChange: (value) => handleThemeFilterChange(theme.id, value),
        options: [
          { value: '', label: 'Все' },
          { value: 'yes', label: 'Да' },
          { value: 'no', label: 'Нет' },
          ...(theme.absolutely_everything
            ? [{ value: 'partial', label: 'Частично' }]
            : []),
        ],
      },
      render: (row) => {
        const topic = row.topics.find((t) => t.id === theme.id);
        let badgeClass = 'bg-secondary';
        let text = '—';
        let details = '';

        if (topic) {
          if (topic.status === 'yes') {
            badgeClass = 'bg-success';
            text = 'Да';
          } else if (topic.status === 'partial') {
            badgeClass = 'bg-warning text-dark';
            text = 'Частично';
          } else if (topic.status === 'no') {
            badgeClass = 'bg-danger';
            text = 'Нет';
          } else if (topic.status === 'unchecked') {
            badgeClass = 'bg-secondary';
            text = '—';
          } else if (topic.status === 'no_text') {
            badgeClass = 'bg-light text-dark border';
            text = 'Нет текста';
          }
          details = topic.details || '';
        }

        return (
          <span
            className={`badge ${badgeClass}`}
            title={details}
            style={{ cursor: 'help' }}
          >
            {text}
          </span>
        );
      },
    })),
    {
      label: 'Лексика',
      key: 'obscene_count',
      filter: {
        type: 'select',
        value: filters.filter_obscene,
        onChange: (value) => handleFilterChange({ target: { name: 'filter_obscene', value } }),
        options: [
          { value: '', label: 'Все' },
          { value: 'yes', label: 'Есть' },
          { value: 'no', label: 'Нет' },
        ],
      },
      render: (row) =>
        row.obscene_count > 0 ? (
          <span className="badge bg-danger" title={row.text}>
            Есть
          </span>
        ) : (
          <span className="badge bg-success">Нет</span>
        ),
    },
  ];

  // === Заглушка при загрузке ===
  if (isFetching) {
    const loadingHeaders = [
      'Дата',
      'Время',
      'Телефон',
      'Оператор',
      'Длительность',
      'Сброс',
      'Текст',
      ...themes.map((t) => t.name),
      'Лексика',
    ].map((label) => ({ label }));

    return (
      <div className="main-content">
        <div className="analytics-header mb-4">
          <h1>Аналитика всех звонков по темам проекта "{project?.name_text}"</h1>
        </div>

        {/* === Кнопка экспорта (задизейблена) === */}
        <div className="mb-3">
          <button className="btn btn-success btn-sm" disabled>
            📥 Скачать Excel
          </button>
        </div>

        {/* === Кнопки фильтров (задизейблены) === */}
        <div className="mb-3 d-flex gap-2 flex-wrap">
          <button className="btn btn-sm btn-secondary" disabled>
            📄 Есть текст оператора
          </button>
          <button className="btn btn-sm btn-warning" disabled>
            ❓ Проверено
          </button>
          <button className="btn btn-danger btn-sm" disabled>
            🗑️ Сбросить
          </button>
        </div>

        {/* === Поля дат (задизейблены) === */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <label className="form-label">Дата от (с секундами)</label>
            <input type="datetime-local" className="form-control form-control-sm" disabled step="1" />
          </div>
          <div className="col-md-3">
            <label className="form-label">Дата до (с секундами)</label>
            <input type="datetime-local" className="form-control form-control-sm" disabled step="1" />
          </div>
        </div>

        <UniversalTable
          headers={headers}
          data={[]}
          isLoading={true}
          isDisabled={true}
          noDataText="Загружаем звонки..."
        />

        <div className="d-flex justify-content-center mt-3">
          <div className="text-muted">Загрузка записей...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="analytics-header mb-4">
        <h1>Аналитика всех звонков по темам проекта "{project?.name_text}"</h1>
      </div>

      {/* === Кнопка экспорта / Прогресс-бар === */}
      <div className="mb-3">
        {isExporting ? (
          <div className="card bg-light p-3">
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '120px', fontSize: '0.9rem' }}>
                Экспорт: {Math.round(exportProgress)}%
              </div>
              <div className="flex-grow-1">
                <div className="progress" style={{ height: '20px' }}>
                  <div
                    className="progress-bar bg-success"
                    role="progressbar"
                    style={{ width: `${exportProgress}%` }}
                    aria-valuenow={exportProgress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    {Math.round(exportProgress)}%
                  </div>
                </div>
              </div>
              <button className="btn btn-sm btn-secondary" disabled>
                Отмена
              </button>
            </div>
            {exportError && (
              <div className="text-danger mt-2">
                <small>❌ Ошибка: {exportError}</small>
              </div>
            )}
          </div>
        ) : (
          <button onClick={exportToExcel} className="btn btn-success btn-sm">
            📥 Скачать Excel
          </button>
        )}
      </div>

      {/* === Фильтры === */}
      <div className="mb-3 d-flex gap-2 flex-wrap">
        <button
          onClick={() =>
            handleFilterChange({
              target: { name: 'filter_no_text', value: filters.filter_no_text ? '' : 'yes' },
            })
          }
          className={`btn btn-sm ${
            filters.filter_no_text ? 'btn-secondary active' : 'btn-secondary'
          }`}
        >
          📄 Есть текст оператора {filters.filter_no_text && '✅'}
        </button>

        <button
          onClick={() =>
            handleFilterChange({
              target: { name: 'filter_unchecked', value: filters.filter_unchecked ? '' : 'yes' },
            })
          }
          className={`btn btn-sm ${
            filters.filter_unchecked ? 'btn-warning active' : 'btn-warning'
          }`}
        >
          ❓ Проверено {filters.filter_unchecked && '✅'}
        </button>

        <button onClick={resetFilters} className="btn btn-danger btn-sm">
          🗑️ Сбросить
        </button>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <label className="form-label">Дата от (с секундами)</label>
          <input
            type="datetime-local"
            name="date_filter_from"
            className="form-control form-control-sm"
            value={tempDateValues.date_filter_from || dateFilters.date_filter_from}
            onChange={handleDateChange}
            onBlur={handleDateBlur}
            step="1"
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">Дата до (с секундами)</label>
          <input
            type="datetime-local"
            name="date_filter_to"
            className="form-control form-control-sm"
            value={tempDateValues.date_filter_to || dateFilters.date_filter_to}
            onChange={handleDateChange}
            onBlur={handleDateBlur}
            step="1"
          />
        </div>
      </div>

      {/* === Универсальная таблица === */}
      <UniversalTable
        headers={headers}
        data={calls}
        isLoading={false}
        noDataText="Записей по данным фильтрам не найдено"
        emptySearchText="Попробуйте изменить параметры фильтрации."
        onRowClick={(row) => setActiveTextSoundId(row.id)}
      />

      {/* === Пагинация === */}
      {totalRecords > 0 && (
        <>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
          <div className="text-muted text-center mt-2">
            Показано {start}–{end} из {totalRecords.toLocaleString()} записей
          </div>
        </>
      )}

      {totalRecords === 0 && !isFetching && (
        <div className="text-muted text-center mt-2">Найдено 0 записей</div>
      )}

      {/* === Модальное окно с текстом === */}
      {activeTextSoundId && (
        <TextModal
          soundId={activeTextSoundId}
          onClose={() => setActiveTextSoundId(null)}
        />
      )}
    </div>
  );
};

export default AnalyticsAllCallsPage;
